// Task completion sound - clean double heartbeat thump
// Works across Chrome, Edge, Safari (desktop & iOS), Firefox

// Shared AudioContext - created lazily on first user interaction
let sharedAudioContext = null;

// Module-level enabled flag. Kept in sync with the "Momentum sounds" toggle
// in Settings so that every sound in this file honors the user's choice.
// Initial value is read from localStorage so a page refresh doesn't unmute
// a user who previously turned sounds off. `SoundManager.setEnabled` (via
// useSoundSettings) mirrors changes here on every toggle.
let soundsEnabled = (() => {
  try {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem('wwi_sounds');
    if (stored === null) return true; // default on
    return stored === 'true';
  } catch (e) {
    return true;
  }
})();

export const setSoundsEnabled = (enabled) => {
  soundsEnabled = !!enabled;
};

const getAudioContext = () => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  
  if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
    sharedAudioContext = new AudioContextClass();
  }
  
  // Resume if suspended (required after user gesture)
  if (sharedAudioContext.state === 'suspended') {
    sharedAudioContext.resume();
  }
  
  return sharedAudioContext;
};

// Double thump sound for task completion
export const playHeartbeatSound = (isMilestone = false, milestoneCount = 0) => {
  if (!soundsEnabled) return;
  try {
    const audioContext = getAudioContext();
    if (!audioContext) return;
    
    const now = audioContext.currentTime;
    const volume = isMilestone ? 2.4 : 2.1; // 3x louder
    
    // Function to create a single clean deep thump
    const createThump = (startTime, vol) => {
      const bass = audioContext.createOscillator();
      const bassGain = audioContext.createGain();
      bass.connect(bassGain);
      bassGain.connect(audioContext.destination);
      bass.frequency.setValueAtTime(80, startTime);
      bass.frequency.exponentialRampToValueAtTime(50, startTime + 0.12);
      bass.type = 'sine';
      
      // Smooth envelope
      bassGain.gain.setValueAtTime(0, startTime);
      bassGain.gain.linearRampToValueAtTime(vol, startTime + 0.008);
      bassGain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.18);
      
      bass.start(startTime);
      bass.stop(startTime + 0.22);
    };
    
    // First thump - heavier
    createThump(now + 0.01, volume);
    
    // Second thump - lighter, after a pause (like a heartbeat)
    createThump(now + 0.22, volume * 0.5);
    
    // Clean completion ding - short, modern, satisfying (the "twang")
    const chimeTime = now + 0.5;
    
    // Main tone - bright but not harsh
    const chime = audioContext.createOscillator();
    const chimeGain = audioContext.createGain();
    chime.connect(chimeGain);
    chimeGain.connect(audioContext.destination);
    
    // Quick rising pitch for satisfying feel (lowered 20%)
    chime.frequency.setValueAtTime(704, chimeTime); // ~F5
    chime.frequency.exponentialRampToValueAtTime(940, chimeTime + 0.08); // ~A#5 - rise up
    chime.type = 'sine';
    
    // Short, punchy envelope
    const chimeVolume = isMilestone ? 0.18 : 0.14;
    chimeGain.gain.setValueAtTime(0, chimeTime);
    chimeGain.gain.linearRampToValueAtTime(chimeVolume, chimeTime + 0.01);
    chimeGain.gain.exponentialRampToValueAtTime(chimeVolume * 0.3, chimeTime + 0.15);
    chimeGain.gain.exponentialRampToValueAtTime(0.001, chimeTime + 0.4);
    
    chime.start(chimeTime);
    chime.stop(chimeTime + 0.5);
    
    // Subtle shimmer overtone (lowered 20%)
    const overtone = audioContext.createOscillator();
    const overtoneGain = audioContext.createGain();
    overtone.connect(overtoneGain);
    overtoneGain.connect(audioContext.destination);
    overtone.frequency.setValueAtTime(1408, chimeTime); // ~F6
    overtone.frequency.exponentialRampToValueAtTime(1880, chimeTime + 0.08);
    overtone.type = 'sine';
    
    overtoneGain.gain.setValueAtTime(0, chimeTime);
    overtoneGain.gain.linearRampToValueAtTime(chimeVolume * 0.08, chimeTime + 0.01);
    overtoneGain.gain.exponentialRampToValueAtTime(0.001, chimeTime + 0.25);
    
    overtone.start(chimeTime);
    overtone.stop(chimeTime + 0.3);
    
    // Pops and hisses beneath the chime as particles float up (reduced 5%)
    const popVolume = isMilestone ? 0.43 : 0.36;
    
    // Mix of pops (short, punchy) and hisses (different speeds)
    const sounds = [
      { time: 0.55, type: 'pop' },
      { time: 0.7, type: 'hiss', speed: 'fast' },
      { time: 0.82, type: 'pop' },
      { time: 0.95, type: 'hiss', speed: 'slow' },
      { time: 1.05, type: 'pop' },
      { time: 1.15, type: 'hiss', speed: 'medium' },
      { time: 1.28, type: 'hiss', speed: 'fast' },
      { time: 1.4, type: 'pop' },
      { time: 1.52, type: 'hiss', speed: 'slow' },
      { time: 1.65, type: 'pop' },
    ];
    
    sounds.forEach(({ time: delay, type, speed }) => {
      const soundTime = now + delay;
      
      if (type === 'pop') {
        // Short punchy pop
        const popDuration = 0.03 + Math.random() * 0.02;
        const bufferSize = Math.floor(audioContext.sampleRate * popDuration);
        const noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
        const noiseData = noiseBuffer.getChannelData(0);
        
        for (let j = 0; j < bufferSize; j++) {
          noiseData[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / bufferSize, 2);
        }
        
        const pop = audioContext.createBufferSource();
        pop.buffer = noiseBuffer;
        
        const filter = audioContext.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.setValueAtTime(1400 + Math.random() * 1000, soundTime);
        filter.Q.setValueAtTime(3, soundTime);
        
        const popGain = audioContext.createGain();
        pop.connect(filter);
        filter.connect(popGain);
        popGain.connect(audioContext.destination);
        
        const thisVolume = popVolume * (0.8 + Math.random() * 0.5);
        popGain.gain.setValueAtTime(thisVolume, soundTime);
        popGain.gain.exponentialRampToValueAtTime(0.001, soundTime + popDuration);
        
        pop.start(soundTime);
        pop.stop(soundTime + popDuration + 0.01);
        
      } else {
        // Hiss - white noise at different speeds/durations
        let hissDuration;
        if (speed === 'fast') {
          hissDuration = 0.06 + Math.random() * 0.03;
        } else if (speed === 'medium') {
          hissDuration = 0.12 + Math.random() * 0.05;
        } else {
          hissDuration = 0.2 + Math.random() * 0.08;
        }
        
        const bufferSize = Math.floor(audioContext.sampleRate * hissDuration);
        const noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
        const noiseData = noiseBuffer.getChannelData(0);
        
        // Smooth envelope for hiss
        for (let j = 0; j < bufferSize; j++) {
          const env = Math.sin((j / bufferSize) * Math.PI); // Bell curve
          noiseData[j] = (Math.random() * 2 - 1) * env;
        }
        
        const hiss = audioContext.createBufferSource();
        hiss.buffer = noiseBuffer;
        
        // Highpass filter for airy hiss character
        const filter = audioContext.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.setValueAtTime(2000 + Math.random() * 1500, soundTime);
        
        const hissGain = audioContext.createGain();
        hiss.connect(filter);
        filter.connect(hissGain);
        hissGain.connect(audioContext.destination);
        
        const hissVolume = popVolume * (0.35 + Math.random() * 0.25);
        hissGain.gain.setValueAtTime(hissVolume, soundTime);
        hissGain.gain.exponentialRampToValueAtTime(0.001, soundTime + hissDuration);
        
        hiss.start(soundTime);
        hiss.stop(soundTime + hissDuration + 0.01);
      }
    });
    
  } catch (e) {
    console.warn('Audio playback failed:', e.message);
  }
};

// Warm two-note "commitment" chime - plays when a task is pinned as the Lock Screen focus.
// Soft, warm, brief. Two notes gently rising - like a small yes.
export const playLockScreenPinSound = () => {
  if (!soundsEnabled) return;
  try {
    const audioContext = getAudioContext();
    if (!audioContext) return;

    const now = audioContext.currentTime;

    // Note 1 - warm, low (E5 = 659Hz)
    const n1 = audioContext.createOscillator();
    const n1Gain = audioContext.createGain();
    n1.connect(n1Gain);
    n1Gain.connect(audioContext.destination);
    n1.type = 'sine';
    n1.frequency.setValueAtTime(659, now);
    n1Gain.gain.setValueAtTime(0, now);
    n1Gain.gain.linearRampToValueAtTime(0.10, now + 0.015);
    n1Gain.gain.exponentialRampToValueAtTime(0.001, now + 0.55);
    n1.start(now);
    n1.stop(now + 0.6);

    // Note 2 - a fifth above (B5 = 988Hz), delayed for a "yes" feel
    const n2 = audioContext.createOscillator();
    const n2Gain = audioContext.createGain();
    n2.connect(n2Gain);
    n2Gain.connect(audioContext.destination);
    n2.type = 'sine';
    n2.frequency.setValueAtTime(988, now + 0.12);
    n2Gain.gain.setValueAtTime(0, now + 0.12);
    n2Gain.gain.linearRampToValueAtTime(0.075, now + 0.135);
    n2Gain.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
    n2.start(now + 0.12);
    n2.stop(now + 0.75);

    // Tiny shimmer overtone (an octave above note 2)
    const shimmer = audioContext.createOscillator();
    const shimmerGain = audioContext.createGain();
    shimmer.connect(shimmerGain);
    shimmerGain.connect(audioContext.destination);
    shimmer.type = 'sine';
    shimmer.frequency.setValueAtTime(1976, now + 0.14);
    shimmerGain.gain.setValueAtTime(0, now + 0.14);
    shimmerGain.gain.linearRampToValueAtTime(0.02, now + 0.155);
    shimmerGain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
    shimmer.start(now + 0.14);
    shimmer.stop(now + 0.5);
  } catch (e) {
    console.warn('Lock screen pin chime failed:', e.message);
  }
};

// "Release + commit" chime - plays when REPLACING an existing Lock Screen focus.
// Three notes: a soft downward "let go" (G5→F#5) followed by the same rising commit
// note (B5). Same warm sine palette as the pin chime so it belongs to the family,
// but the extra downward gesture tells the ear a handoff just happened.
export const playLockScreenReplaceSound = () => {
  if (!soundsEnabled) return;
  try {
    const audioContext = getAudioContext();
    if (!audioContext) return;

    const now = audioContext.currentTime;

    // Note 1 - G5 (784Hz), the "release" (starts, quickly dips)
    const releaseOsc = audioContext.createOscillator();
    const releaseGain = audioContext.createGain();
    releaseOsc.connect(releaseGain);
    releaseGain.connect(audioContext.destination);
    releaseOsc.type = 'sine';
    releaseOsc.frequency.setValueAtTime(784, now);
    releaseOsc.frequency.exponentialRampToValueAtTime(740, now + 0.14); // slide to F#5
    releaseGain.gain.setValueAtTime(0, now);
    releaseGain.gain.linearRampToValueAtTime(0.075, now + 0.02);
    releaseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    releaseOsc.start(now);
    releaseOsc.stop(now + 0.4);

    // Note 2 - B5 (988Hz), the "commit" (arrives after the release settles)
    const commitOsc = audioContext.createOscillator();
    const commitGain = audioContext.createGain();
    commitOsc.connect(commitGain);
    commitGain.connect(audioContext.destination);
    commitOsc.type = 'sine';
    commitOsc.frequency.setValueAtTime(988, now + 0.28);
    commitGain.gain.setValueAtTime(0, now + 0.28);
    commitGain.gain.linearRampToValueAtTime(0.09, now + 0.30);
    commitGain.gain.exponentialRampToValueAtTime(0.001, now + 0.85);
    commitOsc.start(now + 0.28);
    commitOsc.stop(now + 0.9);

    // Same subtle shimmer over the commit note (octave up)
    const shimmer = audioContext.createOscillator();
    const shimmerGain = audioContext.createGain();
    shimmer.connect(shimmerGain);
    shimmerGain.connect(audioContext.destination);
    shimmer.type = 'sine';
    shimmer.frequency.setValueAtTime(1976, now + 0.30);
    shimmerGain.gain.setValueAtTime(0, now + 0.30);
    shimmerGain.gain.linearRampToValueAtTime(0.022, now + 0.315);
    shimmerGain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    shimmer.start(now + 0.30);
    shimmer.stop(now + 0.65);
  } catch (e) {
    console.warn('Lock screen replace chime failed:', e.message);
  }
};

// Counter click chirp - plays when flame counter increments
export const playCounterClickSound = () => {
  if (!soundsEnabled) return;
  try {
    const audioContext = getAudioContext();
    if (!audioContext) return;
    
    const now = audioContext.currentTime;
    
    // Bright chirp/click
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();
    osc.connect(gain);
    gain.connect(audioContext.destination);
    
    // Quick descending chirp
    osc.frequency.setValueAtTime(1200, now);
    osc.frequency.exponentialRampToValueAtTime(600, now + 0.05);
    osc.type = 'sine';
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.15, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
    
    osc.start(now);
    osc.stop(now + 0.1);
  } catch (e) {
    console.warn('Counter click sound failed:', e.message);
  }
};

// Focus swoosh sound - plays during "Find my thread" reset ritual
export const playFocusSwooshSound = () => {
  if (!soundsEnabled) return;
  try {
    const audioContext = getAudioContext();
    if (!audioContext) return;
    
    const now = audioContext.currentTime;
    
    // Create filtered noise for swoosh effect (slowed 2x)
    const duration = 1.2;
    const bufferSize = Math.floor(audioContext.sampleRate * duration);
    const noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
    const noiseData = noiseBuffer.getChannelData(0);
    
    // Generate white noise with envelope
    for (let i = 0; i < bufferSize; i++) {
      const t = i / bufferSize;
      // Envelope: quick attack, sustained middle, smooth decay
      const env = Math.sin(t * Math.PI) * Math.pow(1 - t * 0.3, 2);
      noiseData[i] = (Math.random() * 2 - 1) * env;
    }
    
    const noise = audioContext.createBufferSource();
    noise.buffer = noiseBuffer;
    
    // Bandpass filter that sweeps for swoosh effect (slowed 2x, 10% lower pitch)
    const filter = audioContext.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.setValueAtTime(2, now);
    // Sweep frequency from low to high for "focusing in" feel
    filter.frequency.setValueAtTime(180, now);
    filter.frequency.exponentialRampToValueAtTime(720, now + 0.3);
    filter.frequency.exponentialRampToValueAtTime(1800, now + 0.7);
    filter.frequency.exponentialRampToValueAtTime(360, now + duration);
    
    // Volume envelope (reduced 10%)
    const gain = audioContext.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.225, now + 0.1);
    gain.gain.linearRampToValueAtTime(0.18, now + 0.6);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(audioContext.destination);
    
    noise.start(now);
    noise.stop(now + duration + 0.1);
    
    // Add a subtle low tone underneath for depth (slowed 2x, 10% lower pitch)
    const lowTone = audioContext.createOscillator();
    const lowGain = audioContext.createGain();
    lowTone.connect(lowGain);
    lowGain.connect(audioContext.destination);
    
    lowTone.frequency.setValueAtTime(108, now);
    lowTone.frequency.exponentialRampToValueAtTime(72, now + duration);
    lowTone.type = 'sine';
    
    lowGain.gain.setValueAtTime(0, now);
    lowGain.gain.linearRampToValueAtTime(0.108, now + 0.2);
    lowGain.gain.exponentialRampToValueAtTime(0.001, now + duration * 0.8);
    
    lowTone.start(now);
    lowTone.stop(now + duration);
    
  } catch (e) {
    console.warn('Swoosh sound failed:', e.message);
  }
};

// Double thump only - plays when showing "Here's where you were"
export const playThumpWithSwoosh = () => {
  if (!soundsEnabled) return;
  try {
    const audioContext = getAudioContext();
    if (!audioContext) return;
    
    const now = audioContext.currentTime;
    const volume = 2.1;
    
    // Function to create a single clean deep thump
    const createThump = (startTime, vol) => {
      const bass = audioContext.createOscillator();
      const bassGain = audioContext.createGain();
      bass.connect(bassGain);
      bassGain.connect(audioContext.destination);
      bass.frequency.setValueAtTime(80, startTime);
      bass.frequency.exponentialRampToValueAtTime(50, startTime + 0.12);
      bass.type = 'sine';
      
      // Smooth envelope
      bassGain.gain.setValueAtTime(0, startTime);
      bassGain.gain.linearRampToValueAtTime(vol, startTime + 0.008);
      bassGain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.18);
      
      bass.start(startTime);
      bass.stop(startTime + 0.22);
    };
    
    // First thump - heavier
    createThump(now + 0.01, volume);
    
    // Second thump - lighter, after a pause (like a heartbeat)
    createThump(now + 0.22, volume * 0.5);
    
  } catch (e) {
    console.warn('Thump sound failed:', e.message);
  }
};
